import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  captureVehicleWeight,
  getWeighBridgeWorkCenters,
  getTargetVsActualConfigForWorkflowStage,
} from 'src/redux/Actions/VehicleTracking';
import {
  getCapturedWeightForWeightBridgeEventUuid,
  getDataForVehicleTrackingLoadCards,
  getVehicleTrackingAttachedLoads,
  updateVehicleTrackingLoadWeight,
} from 'src/redux/Actions/VehicleTrackingOutward';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as VehicleWeightIcon } from 'src/Images/icons/common/VehicleWeightIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';

function VariancePill({ percentage, difference, uom }) {
  if (percentage === null) return null;
  return (
    <span
      className={`text-md status-pill ${percentage >= 0 ? 'green' : 'red'}`}
    >
      {percentage > 0 ? '+' : ''}
      {percentage}% Variance
      {difference !== null &&
        ` (${difference > 0 ? '+' : ''}${difference} ${uom || ''})`}
    </span>
  );
}

const LOAD_STATUS = {
  TARE_WEIGHT: 1,
  CONFIRM_LOADING: 2,
  GROSS_WEIGHT: 3,
};

function getCardStates(
  status,
  cardTareWeight,
  cardGrossWeight,
  completed,
  nextWeightToPrefill,
) {
  const statusId = status?.id;

  if (completed) {
    return {
      cardTareWeight: {
        isActive: false,
        captureDisabled: true,
        confirmDisabled: true,
      },
      confirmLoading: { isActive: false, confirmDisabled: true },
      cardGrossWeight: {
        isActive: false,
        captureDisabled: true,
        confirmDisabled: true,
      },
    };
  }

  return {
    cardTareWeight: {
      isActive: statusId === LOAD_STATUS.TARE_WEIGHT,
      captureDisabled: statusId !== LOAD_STATUS.TARE_WEIGHT,
      confirmDisabled:
        statusId !== LOAD_STATUS.TARE_WEIGHT ||
        (cardTareWeight == null && !nextWeightToPrefill),
    },
    confirmLoading: {
      isActive: statusId === LOAD_STATUS.CONFIRM_LOADING,
      confirmDisabled: statusId !== LOAD_STATUS.CONFIRM_LOADING,
    },
    cardGrossWeight: {
      isActive: statusId === LOAD_STATUS.GROSS_WEIGHT,
      captureDisabled: statusId !== LOAD_STATUS.GROSS_WEIGHT,
      confirmDisabled:
        statusId !== LOAD_STATUS.GROSS_WEIGHT ||
        (cardGrossWeight == null && !nextWeightToPrefill),
    },
  };
}

export default function VehicleTrackingManageGrossWeight({
  onBack,
  themeColor,
  vtLoadHeaderUniqueNumber,
}) {
  const { workflowStageId, vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const canCaptureLoadGrossWeight = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CAPTURE_LOAD_WEIGHT_2,
  );
  const canConfirmLoadWeight = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CONFIRM_LOAD_WEIGHT,
  );

  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const { vehicleTrackingLoadDetails, vehicleTrackingAttachedLoads } =
    VehicleTrackingOutwardReducer;
  const { weighBridgeWorkCenters, vehicleTrackingDetails } =
    vehicleTrackingDataReducer;

  const [selectedWeightBridge, setSelectedWeightBridge] = useState(null);
  const [captureWeight, setCaptureWeight] = useState(false);
  const [weightBridgeEventUUID, setWeightBridgeEventUUID] = useState(null);
  const [captureWeightLoading, setCaptureWeightLoading] = useState(false);
  const [capturedGrossWeight, setCapturedGrossWeight] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [nextWeightToPrefill, setNextWeightToPrefill] = useState(null);

  // Derived from status?.id: 1 → 'Tare Weight', 3 → 'Gross Weight'
  const cardFieldName =
    vehicleTrackingLoadDetails?.status?.id === 3
      ? 'Gross Weight'
      : 'Tare Weight';

  const fieldVerboseName = cardFieldName;
  const weightBridgeOptions = useMemo(
    () =>
      weighBridgeWorkCenters?.map((option) => ({
        value: option.id,
        label: option.name,
        ...option,
      })) || [],
    [weighBridgeWorkCenters],
  );

  const loadTareWeight = vehicleTrackingLoadDetails?.actualTareWeight;
  const loadGrossWeight =
    capturedGrossWeight ?? vehicleTrackingLoadDetails?.actualGrossWeight;

  const calcVariance = (planned, actual) => {
    const p = Number(planned);
    const a = Number(actual);
    return p > 0 && actual != null && Number.isFinite(a)
      ? Number((((a - p) / p) * 100).toFixed(1))
      : null;
  };

  const actualLoadingWeight =
    loadTareWeight != null && loadGrossWeight != null
      ? Number((Number(loadGrossWeight) - Number(loadTareWeight)).toFixed(2))
      : null;

  const loadVariance = calcVariance(
    vehicleTrackingLoadDetails?.plannedLoadGrossWeight,
    actualLoadingWeight,
  );
  const grossWeightVariance = calcVariance(
    vehicleTrackingLoadDetails?.plannedGrossWeight,
    loadGrossWeight,
  );

  const plannedLoadWeight = Number(
    vehicleTrackingLoadDetails?.plannedLoadGrossWeight,
  );
  const loadDifference =
    vehicleTrackingLoadDetails?.plannedLoadGrossWeight != null &&
    actualLoadingWeight != null
      ? Number((actualLoadingWeight - plannedLoadWeight).toFixed(2))
      : null;

  const plannedGrossWeightNum = Number(
    vehicleTrackingLoadDetails?.plannedGrossWeight,
  );
  const grossWeightDifference =
    vehicleTrackingLoadDetails?.plannedGrossWeight != null &&
    loadGrossWeight != null
      ? Number((Number(loadGrossWeight) - plannedGrossWeightNum).toFixed(2))
      : null;

  const isLoadCompleted = vehicleTrackingLoadDetails?.completed === true;

  let cardStates = null;

  if (vehicleTrackingLoadDetails) {
    cardStates = getCardStates(
      vehicleTrackingLoadDetails?.status,
      loadTareWeight,
      loadGrossWeight,
      vehicleTrackingLoadDetails?.completed,
      nextWeightToPrefill,
    );
  }

  useEffect(() => {
    dispatch(getWeighBridgeWorkCenters(tenantOrganisationId, '1'));
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (!selectedWeightBridge && weightBridgeOptions.length > 0) {
      setSelectedWeightBridge(weightBridgeOptions[0]);
    }
  }, [selectedWeightBridge, weightBridgeOptions]);

  useEffect(() => {
    if (
      !tenantOrganisationId ||
      !vehicleTrackingNumber ||
      !vtLoadHeaderUniqueNumber
    )
      return;

    dispatch(
      getDataForVehicleTrackingLoadCards(
        tenantOrganisationId,
        vehicleTrackingNumber,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }, [tenantOrganisationId, vehicleTrackingNumber, vtLoadHeaderUniqueNumber]);

  useEffect(() => {
    const loadList = vehicleTrackingAttachedLoads?.vehicleTrackingLoadData;
    const isFirstLoadTransaction = loadList?.every(
      (loadObj) => loadObj?.load_sequence === null,
    );

    // Case 1: Initial case, i.e., no load has been managed yet
    if (
      vehicleTrackingAttachedLoads?.isLoadSequenceEnabled === false &&
      isFirstLoadTransaction
    ) {
      setNextWeightToPrefill(vehicleTrackingDetails?.tareWeight ?? null);
      return;
    }

    // Case 2: A managed load already exists. Find the highest load_sequence
    // and use its actual_load_gross_weight for prefill
    if (
      vehicleTrackingAttachedLoads?.isLoadSequenceEnabled === false &&
      !isFirstLoadTransaction
    ) {
      const highestSequenceLoad = loadList
        ?.filter((load) => load.load_sequence !== null)
        .reduce((maxLoad, currentLoad) =>
          currentLoad.load_sequence > maxLoad.load_sequence
            ? currentLoad
            : maxLoad,
        );
      setNextWeightToPrefill(highestSequenceLoad?.actual_gross_weight ?? null);
    }
  }, [vehicleTrackingAttachedLoads, vehicleTrackingDetails]);

  useEffect(() => {
    if (!vehicleTrackingLoadDetails) return;
    setCapturedGrossWeight(
      vehicleTrackingLoadDetails?.actualGrossWeight ?? null,
    );
  }, [vehicleTrackingLoadDetails]);

  useEffect(() => {
    if (!captureWeight) return undefined;

    let attemptCounter = 0;

    const checkWeightBridgeData = () => {
      dispatch(
        getCapturedWeightForWeightBridgeEventUuid(
          tenantOrganisationId,
          vtLoadHeaderUniqueNumber,
          weightBridgeEventUUID,
          vehicleTrackingNumber,
          fieldVerboseName,
        ),
      ).then((data) => {
        const hasNullActualValue = data?.actualValue == null;

        if (!hasNullActualValue) {
          setCapturedGrossWeight(data?.actualValue);
        }

        if (hasNullActualValue && attemptCounter < 5) {
          attemptCounter += 1;
          setTimeout(checkWeightBridgeData, 5000);
        } else {
          setCaptureWeight(false);
          setCaptureWeightLoading(false);
        }
      });
    };

    setCaptureWeightLoading(true);
    checkWeightBridgeData();

    return () => setCaptureWeight(false);
  }, [captureWeight]);

  function handleCaptureWeight() {
    if (!canCaptureLoadGrossWeight || !selectedWeightBridge) return;

    const uuid = crypto.randomUUID();
    setWeightBridgeEventUUID(uuid);
    setCaptureWeight(true);

    dispatch(
      captureVehicleWeight(
        tenantOrganisationId,
        vehicleTrackingNumber,
        fieldVerboseName,
        'vehicletrackingloadheader',
        workflowStageId,
        selectedWeightBridge?.id,
        uuid,
        vtLoadHeaderUniqueNumber,
      ),
    );
  }

  function handleConfirmWeight() {
    if (!canConfirmLoadWeight) return;

    const weightValue =
      capturedGrossWeight ?? vehicleTrackingLoadDetails?.actualGrossWeight;
    if (weightValue == null) return;

    setIsConfirming(true);

    const updateLoadWeightPayload = {
      weight_value: weightValue,
      vt_load_header_unique_number: vtLoadHeaderUniqueNumber,
      vehicle_tracking_number: vehicleTrackingNumber,
      field_verbose_name: cardFieldName,
    };

    dispatch(updateVehicleTrackingLoadWeight(updateLoadWeightPayload))
      .then(() => {
        dispatch(
          getDataForVehicleTrackingLoadCards(
            tenantOrganisationId,
            vehicleTrackingNumber,
            vtLoadHeaderUniqueNumber,
          ),
        );
        dispatch(
          getVehicleTrackingAttachedLoads(
            tenantOrganisationId,
            vehicleTrackingNumber,
          ),
        );
        dispatch(
          getTargetVsActualConfigForWorkflowStage(
            workflowStageId,
            vehicleTrackingNumber,
          ),
        );
        onBack();
      })
      .finally(() => {
        setIsConfirming(false);
      });
  }

  useEffect(
    () => () => {
      setNextWeightToPrefill(null);
      setCapturedGrossWeight(null);
    },
    [],
  );

  const statusName = vehicleTrackingLoadDetails?.status?.name || 'Gross Weight';
  const loadCode = vehicleTrackingLoadDetails?.loadCode || '--';
  const displayWeight =
    capturedGrossWeight ?? vehicleTrackingLoadDetails?.actualGrossWeight;
  const weightUom = vehicleTrackingLoadDetails?.actualGrossWeightUom || 'KG';
  const confirmWeightEnabled =
    displayWeight != null && !isConfirming && canConfirmLoadWeight;

  return (
    <div className="div-flex-column rg-10 h-100">
      {/* Header */}
      <div className="div-flex-row div-space-between div-align-center padding-top-10">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">{statusName}</span>
          </div>
          <span className="light-blue-pill text-md">{loadCode}</span>
        </div>
        <button
          className={`vehicle-data-form-submit-btn theme-background-${themeColor}`}
          onClick={handleConfirmWeight}
          disabled={!confirmWeightEnabled}
        >
          <span className="text-md">
            {isConfirming ? 'Confirming...' : 'Confirm Weight'}
          </span>
        </button>
      </div>

      {/* Weight gauge */}
      <div className="weight-icon-container">
        <div
          className={`vehicle-weight-gauge ${
            displayWeight == null ? 'no-progress' : 'has-progress'
          }`}
        >
          <VehicleWeightIcon className="vehicle-weight-svg" />
          <div className="vehicle-weight-content">
            <div className="text-xl text-semi-bold w-100 text-nowrap">
              {statusName}
            </div>
            <div className="vehicle-weight-value text-xl text-semi-bold">
              {captureWeightLoading ? (
                <div className="capture-weight-animation">
                  <span className="capture-weight-loading-dot" />
                  <span className="capture-weight-loading-dot" />
                  <span className="capture-weight-loading-dot" />
                </div>
              ) : (
                <div className="div-flex-row div-align-center cg-5">
                  <span>{displayWeight ?? '0'}</span>
                  {displayWeight != null && <span>{weightUom}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Load status cards */}
      <Row className="m-0 g-3 vertical-padding-10">
        <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
          <div
            className={`manage-load-card ${!cardStates?.cardTareWeight?.isActive || isLoadCompleted ? 'manage-load-card-inactive' : ''}`}
          >
            <span className="manage-load-card-badge text-semi-bold">
              Weight Bridge
            </span>
            <div className="div-flex-column rg-5">
              <span className="text-md text-semi-bold">Tare Weight</span>
              <div className="div-flex-row cg-5 div-align-center">
                <TargetInfoIcon className="icon-size-sm" />
                <span className="text-md text-black text-semi-bold">
                  Weight before loading
                </span>
              </div>
            </div>
            <span className="text-md status-pill blue">
              {loadTareWeight
                ? `${loadTareWeight} ${vehicleTrackingLoadDetails?.actualTareWeightUom || 'KG'}`
                : ''}
            </span>
          </div>
        </Col>

        <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
          <div
            className={`manage-load-card ${!cardStates?.confirmLoading?.isActive || isLoadCompleted ? 'manage-load-card-inactive' : ''}`}
          >
            <span className="manage-load-card-badge text-semi-bold">
              Confirm Loading
            </span>
            <div className="div-flex-column rg-5">
              <span className="text-md text-semi-bold">Confirm Loading</span>
              <div className="div-flex-row cg-5 div-align-center">
                <TargetInfoIcon className="icon-size-sm" />
                <span className="text-md text-black text-semi-bold">
                  Planned {vehicleTrackingLoadDetails?.plannedLoadGrossWeight}
                </span>
              </div>
            </div>
            <div className="div-flex-row cg-5 div-align-center">
              <span className="text-sm status-pill red">
                {actualLoadingWeight != null
                  ? `${actualLoadingWeight} ${vehicleTrackingLoadDetails?.actualGrossWeightUom}`
                  : '--'}
              </span>
              <VariancePill
                percentage={loadVariance}
                difference={loadDifference}
                uom={vehicleTrackingLoadDetails?.actualGrossWeightUom}
              />
            </div>
          </div>
        </Col>

        <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
          <div
            className={`manage-load-card ${!cardStates?.cardGrossWeight?.isActive || isLoadCompleted ? 'manage-load-card-inactive' : ''}`}
          >
            <span className="manage-load-card-badge text-semi-bold">
              Weight Bridge
            </span>
            <div className="div-flex-column rg-5">
              <span className="text-md text-semi-bold">Gross Weight</span>
              <div className="div-flex-row cg-5 div-align-center">
                <TargetInfoIcon className="icon-size-sm" />
                <span className="text-md text-black text-semi-bold">
                  Planned {vehicleTrackingLoadDetails?.plannedGrossWeight}
                </span>
              </div>
            </div>
            <div className="div-flex-row cg-5 div-align-center">
              <span className="text-md status-pill red">
                {loadGrossWeight
                  ? `${loadGrossWeight} ${vehicleTrackingLoadDetails?.actualGrossWeightUom || 'KG'}`
                  : '--'}
              </span>
              <VariancePill
                percentage={grossWeightVariance}
                difference={grossWeightDifference}
                uom={vehicleTrackingLoadDetails?.actualGrossWeightUom}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Info banner */}
      {canCaptureLoadGrossWeight && (
        <>
          {/* Info banner */}
          <div className="bg-light-grey text-md div-flex-row cg-5 div-align-center p-1">
            <TargetInfoIcon className="icon-size-sm" />
            To update the gross weight select the weight bridge below &amp;
            capture weight again.
          </div>

          {/* Select Weight Bridge + Capture */}
          <div className="div-flex-row div-align-center div-space-between">
            <Col xxl={4} xl={4} lg={4} md={4} className="form-input-container">
              <div className="div-flex-column">
                <div className="div-flex-row">
                  <span className="text-sm text-bold">
                    Select Weight Bridge
                  </span>
                </div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    options={weightBridgeOptions}
                    value={selectedWeightBridge}
                    onChange={(selectedOption) =>
                      setSelectedWeightBridge(selectedOption)
                    }
                    placeholder="Select a weight bridge..."
                    isSearchable
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </div>
            </Col>

            <button
              className={`vehicle-data-form-submit-btn theme-background-${themeColor}`}
              onClick={handleCaptureWeight}
              disabled={!selectedWeightBridge || captureWeightLoading}
            >
              <span className="text-md">
                {captureWeightLoading ? 'Capturing...' : 'Capture Weight'}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
